import { useState } from 'react';
import { X, Send, Loader2, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { publicApi } from '../../lib/api';
import { cn } from '../../lib/utils';

const destinations = [
  'Maldives', 'Switzerland', 'Paris', 'Bali', 'Dubai', 
  'Singapore', 'Thailand', 'Kerala', 'Goa', 'Rajasthan', 'Other'
];

export function QuickEnquiry() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    destination: '',
    travel_date: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!/^[\d\s+-]{10,}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.destination) newErrors.destination = 'Please select a destination';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await publicApi.createEnquiry({
        ...formData,
        package_name: `Enquiry for ${formData.destination}`,
        number_of_travelers: { adults: 2, children: 0 }
      });
      setIsSuccess(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsSuccess(false);
        setFormData({
          name: '',
          phone: '',
          email: '',
          destination: '',
          travel_date: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-40 flex items-center gap-2',
          'bg-[#FF6B35] hover:bg-[#E85A2D] text-white',
          'px-6 py-4 rounded-full shadow-lg hover:shadow-xl',
          'transition-all duration-300 group',
          isOpen && 'opacity-0 pointer-events-none'
        )}
        data-testid="quick-enquiry-btn"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="font-medium">Quick Enquiry</span>
      </button>

      {/* Enquiry Form Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center p-4',
          'transition-all duration-300',
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => !isSubmitting && setIsOpen(false)}
        />

        {/* Form Card */}
        <div
          className={cn(
            'relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden',
            'transform transition-all duration-300',
            isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          )}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#004E64] to-[#006B87] p-6 text-white">
            <button
              onClick={() => !isSubmitting && setIsOpen(false)}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quick Enquiry
            </h3>
            <p className="text-white/80 text-sm">Fill in your details and we&apos;ll get back to you!</p>
          </div>

          {/* Success State */}
          {isSuccess ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h4 className="text-xl font-semibold text-[#1A1A1A] mb-2">Thank You!</h4>
              <p className="text-[#4A4A4A]">We&apos;ve received your enquiry and will contact you shortly.</p>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <Input
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={cn(errors.name && 'border-red-500')}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Phone & Email Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    placeholder="Phone *"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={cn(errors.phone && 'border-red-500')}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={cn(errors.email && 'border-red-500')}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Destination */}
              <div>
                <Select
                  value={formData.destination}
                  onValueChange={(value) => setFormData({ ...formData, destination: value })}
                >
                  <SelectTrigger className={cn(errors.destination && 'border-red-500')}>
                    <SelectValue placeholder="Select Destination *" />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.map((dest) => (
                      <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.destination && <p className="text-red-500 text-xs mt-1">{errors.destination}</p>}
              </div>

              {/* Travel Date */}
              <Input
                type="date"
                placeholder="Travel Date"
                value={formData.travel_date}
                onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />

              {/* Message */}
              <Textarea
                placeholder="Your message or requirements..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
              />

              {errors.submit && (
                <p className="text-red-500 text-sm text-center">{errors.submit}</p>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white py-6 rounded-xl"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> Submit Enquiry</>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
